/**
 * 派任务 —— 云端【能】指挥全队的那条正路。
 *
 * ★★ 云端指挥不等于遥控。两者的界线不是"胆子大小", 是这套系统的设计前提:
 *
 *   台账(本面板)      hub 派任务 → 设备自己认领、自己执行、带证据回报。
 *                    断网时设备照常干完, 恢复后对账 —— 这正是"必须全局唯一"的那类事,
 *                    也是 hub 存在的理由(design-cloud-hub-edge-autonomy.md)。
 *   遥控行走/急停      **不做**。会让狗动的操作要人在现场(CLAUDE.md §0.4);
 *                    而且一条要跨公网才生效的"急停"本身就是个危险的承诺。
 *
 * 所以这一面板只做三件事:把任务写进台账、看谁认领了、看进度与证据。
 * 它不发一条运动指令。
 */
import { useState } from 'react';
import { hub, HubError, type UnitSpec } from '@/lib/hubClient';
import type { MapPlace } from './MapFrameView';
import type { Terminal } from '@/types/fleet';

interface Props {
  terminals: Terminal[];
  places: MapPlace[];
  mapId: string | null;
  /** 在地图上选中的地点(点一下地点行会填进来), 让"去这儿看看"两步就能派出去。 */
  presetPlace?: string | null;
  onDispatched: () => void;
}

const KINDS = [
  { k: 'inspect', label: '巡检' },
  { k: 'explore', label: '探索' },
  { k: 'find_object', label: '找物' },
  { k: 'custom', label: '自定义' },
];

export default function DispatchPanel({ terminals, places, mapId, presetPlace, onDispatched }: Props) {
  const [goal, setGoal] = useState('');
  const [kind, setKind] = useState('inspect');
  const [targets, setTargets] = useState<string[]>(presetPlace ? [presetPlace] : []);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [ok, setOk] = useState('');

  const toggle = (name: string) =>
    setTargets((prev) => (prev.includes(name) ? prev.filter((x) => x !== name) : [...prev, name]));

  const submit = async () => {
    if (busy || !goal.trim() || targets.length === 0) return;
    setBusy(true); setErr(''); setOk('');
    // 一个地点 = 一个工作单元。★ 单元是【认领的粒度】: 多台设备可以各领一个,
    // 于是"三台狗分头巡三个点"不需要云端做任何调度 —— 台账就是调度。
    const units: UnitSpec[] = targets.map((name, i) => {
      const p = places.find((x) => x.name === name);
      return {
        kind, label: name, seq: i + 1,
        map_id: mapId || undefined,
        // 坐标一起写进去:设备认领后不必再猜"哪个地点叫这个名字"。
        geometry: p ? JSON.stringify({ x: p.x, y: p.y }) : undefined,
        lease_ms: 10 * 60 * 1000,
      };
    });
    try {
      await hub.createTask({ goal: goal.trim(), kind, mapId: mapId || undefined, units });
      setOk(`已派出:${units.length} 个单元进台账, 等设备认领`);
      setGoal(''); setTargets([]);
      onDispatched();
    } catch (e) {
      setErr(e instanceof HubError && e.status === 403
        ? '仅座舱(admin 令牌)可派任务 —— 设备只能认领, 不能派活给别人'
        : e instanceof Error ? e.message : String(e));
    } finally { setBusy(false); }
  };

  const online = terminals.filter((t) => t.status === 'online').length;

  return (
    <div className="space-y-3">
      <div className="px-2.5 py-2 rounded-md bg-background-50 border border-background-200/70">
        <p className="text-[10px] text-foreground-500 leading-relaxed">
          派任务走【台账】:写进去, 设备自己认领、自己执行、带证据回报 —— 断网也能干完,
          恢复后对账。云端不发运动指令(急停/遥控要人在现场, §0.4)。
          此刻在线 <span className="font-mono text-foreground-700">{online}</span>/{terminals.length} 台可认领。
        </p>
      </div>

      <div>
        <label className="block text-[10px] text-foreground-500 mb-1">任务目标(人话)</label>
        <input value={goal} onChange={(e) => setGoal(e.target.value)}
          placeholder="例:把一层这几个点巡一遍, 拍照留证"
          className="w-full px-2.5 py-1.5 text-[11px] rounded-md bg-background-50 border border-background-200/70 outline-none focus:border-primary-400" />
      </div>

      <div>
        <label className="block text-[10px] text-foreground-500 mb-1">类型</label>
        <div className="flex gap-1 flex-wrap">
          {KINDS.map((k) => (
            <button key={k.k} onClick={() => setKind(k.k)}
              className={`px-2 py-1 rounded text-[10px] cursor-pointer transition-all ${
                kind === k.k ? 'bg-primary-500 text-white' : 'bg-background-100 text-foreground-500'}`}>
              {k.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-[10px] text-foreground-500 mb-1">
          去哪(每选一个地点 = 一个可被单独认领的单元)
        </label>
        {places.length === 0 ? (
          // 空状态要指路(规格 §1.6), 不是画个插图。
          <p className="text-[10px] text-foreground-400 py-2">
            这张图上还没有命名地点 —— 地点由人在设备上教(「记住这里叫充电桩」), 之后会同步上来。
          </p>
        ) : (
          <div className="flex flex-wrap gap-1">
            {places.map((p) => (
              <button key={p.name} onClick={() => toggle(p.name)}
                className={`px-2 py-1 rounded text-[10px] cursor-pointer transition-all ${
                  targets.includes(p.name) ? 'bg-accent-500 text-white' : 'bg-background-100 text-foreground-600'}`}>
                {p.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {err && <p className="text-[10px] text-red-600">{err}</p>}
      {ok && <p className="text-[10px] text-accent-600">{ok}</p>}

      <button onClick={submit} disabled={busy || !goal.trim() || targets.length === 0}
        className={`w-full px-3 py-2 rounded-md text-[11px] font-medium transition-all ${
          busy || !goal.trim() || targets.length === 0
            ? 'bg-background-100 text-foreground-400 cursor-not-allowed'
            : 'bg-primary-500 text-white hover:bg-primary-600 cursor-pointer'}`}>
        {busy ? '写入台账…' : `派出 ${targets.length} 个单元`}
      </button>
    </div>
  );
}
